import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Search, Phone, Images, Network, Clock,
  MessageSquareText, LogOut, ShieldCheck, Menu, Globe, FileText,
  FolderKanban, Bell, Users, ScrollText, Boxes, Waypoints, Map, Dna, Newspaper,
  Languages, Siren, Microscope, Zap, MessageSquareWarning,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale, LOCALES } from "@/contexts/LocaleContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const BASE_NAV = [
  { to: "/", label: "Home", labelKey: "nav.home", icon: LayoutDashboard, testid: "nav-home", end: true },
  { to: "/leads", label: "Lead Room", labelKey: "nav.leads", icon: Microscope, testid: "nav-leads" },
  { to: "/capture", label: "Field capture", labelKey: "nav.fieldCapture", icon: Zap, testid: "nav-capture" },
  { to: "/digest", label: "Daily digest", labelKey: "nav.digest", icon: Newspaper, testid: "nav-digest" },
  { to: "/search", label: "Search", labelKey: "nav.search", icon: Search, testid: "nav-search" },
  { to: "/ads", label: "Advertisements", labelKey: "nav.ads", icon: FileText, testid: "nav-ads" },
  { to: "/cases", label: "Cases", labelKey: "nav.cases", icon: FolderKanban, testid: "nav-cases" },
  { to: "/intel/entities", label: "Intelligence graph", labelKey: "nav.intel", icon: Boxes, testid: "nav-intel" },
  { to: "/intel/graph", label: "Network explorer", labelKey: "nav.networkExplorer", icon: Waypoints, testid: "nav-network" },
  { to: "/intel/networks", label: "Networks", labelKey: "nav.networks", icon: Dna, testid: "nav-networks" },
  { to: "/intel/geo", label: "Geographic", labelKey: "nav.geo", icon: Map, testid: "nav-geo" },
  { to: "/lookup/phone", label: "Phone lookup", labelKey: "nav.phoneLookup", icon: Phone, testid: "nav-phone" },
  { to: "/lookup/image", label: "Image clusters", labelKey: "nav.imageClusters", icon: Images, testid: "nav-images" },
  { to: "/graph", label: "Relationship graph", labelKey: "nav.relationshipGraph", icon: Network, testid: "nav-graph" },
  { to: "/timeline", label: "Timeline", labelKey: "nav.timeline", icon: Clock, testid: "nav-timeline" },
  { to: "/alerts", label: "Alerts", labelKey: "nav.alerts", icon: Bell, testid: "nav-alerts" },
  { to: "/assistant", label: "AI Assistant", labelKey: "nav.assistant", icon: MessageSquareText, testid: "nav-assistant" },
  { to: "/crawler", label: "Crawler", labelKey: "nav.crawler", icon: Globe, testid: "nav-crawler" },
];
const ADMIN_NAV = [
  { to: "/admin/users", label: "Users & roles", labelKey: "nav.users", icon: Users, testid: "nav-users" },
  { to: "/admin/police-roster", label: "Police roster", labelKey: "nav.policeRoster", icon: Siren, testid: "nav-police-roster" },
  { to: "/admin/citizen-reports", label: "Citizen reports", labelKey: "nav.citizenReports", icon: MessageSquareWarning, testid: "nav-citizen-reports" },
  { to: "/admin/audit", label: "Audit log", labelKey: "nav.audit", icon: ScrollText, testid: "nav-audit" },
];

function NavItems({ items, onNavigate, suffix }) {
  return items.map(({ to, label, labelKey, icon: Icon, testid, end }) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      data-testid={`${testid}-${suffix}`}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
          isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100 hover:text-slate-900",
        )
      }
    >
      <Icon className="h-4 w-4" />
      {labelKey ? <Translated k={labelKey} fallback={label} /> : label}
    </NavLink>
  ));
}

function Translated({ k, fallback }) {
  const { t } = useLocale();
  const out = t(k);
  return <>{out === k ? fallback : out}</>;
}

function SidebarContent({ onLogout, user, onNavigate, suffix }) {
  const isAdmin = user?.role === "admin";
  const { t } = useLocale();
  return (
    <div className="h-full flex flex-col">
      <div className="px-5 py-5 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 grid place-items-center bg-slate-900 text-white rounded-md font-display font-bold">M</div>
          <div>
            <div className="font-display font-bold tracking-tight text-base leading-none">MUKTI</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1">Intelligence Platform</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        <NavItems items={BASE_NAV} onNavigate={onNavigate} suffix={suffix} />
        {isAdmin && (
          <>
            <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.18em] text-slate-400 font-mono">
              {t("nav.admin")}
            </div>
            <NavItems items={ADMIN_NAV} onNavigate={onNavigate} suffix={suffix} />
          </>
        )}
      </nav>

      <div className="border-t border-slate-200 p-3 space-y-2">
        <div className="flex items-center gap-2 px-1">
          <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-800 grid place-items-center text-xs font-semibold font-mono">
            {(user?.name || "I").slice(0, 1)}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{user?.name || "Investigator"}</div>
            <div className="text-[11px] text-slate-500 truncate">{user?.role || "investigator"}</div>
          </div>
        </div>
        <Button data-testid={`logout-btn-${suffix}`} variant="outline" size="sm" className="w-full justify-start gap-2 border-slate-300" onClick={onLogout}>
          <LogOut className="h-4 w-4" /> {t("common.signOut")}
        </Button>
      </div>
    </div>
  );
}

function NotificationBell() {
  const [count, setCount] = useState(0);
  const refresh = useCallback(() => {
    api.get("/alerts/unread_count").then(({ data }) => setCount(data.count || 0)).catch(() => {});
  }, []);
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30000);
    window.addEventListener("mukti-alerts-changed", refresh);
    return () => { clearInterval(id); window.removeEventListener("mukti-alerts-changed", refresh); };
  }, [refresh]);
  return (
    <Link to="/alerts" data-testid="notification-bell" className="relative h-9 w-9 grid place-items-center rounded-md border border-slate-200 hover:bg-slate-50">
      <Bell className="h-4 w-4 text-slate-600" />
      {count > 0 && (
        <span data-testid="unread-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-red-600 text-white text-[10px] font-mono">
          {count > 99 ? "99+" : count}
        </span>
      )}
    </Link>
  );
}

function LanguageSwitcher() {
  const { locale, setLocale } = useLocale();
  return (
    <div className="relative" data-testid="lang-switcher-wrap">
      <select
        data-testid="lang-switcher"
        value={locale}
        onChange={(e) => setLocale(e.target.value)}
        className="h-9 pl-7 pr-3 text-xs border border-slate-200 rounded-md bg-white hover:bg-slate-50 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        aria-label="Language"
      >
        {LOCALES.map((l) => (
          <option key={l.code} value={l.code}>{l.native}</option>
        ))}
      </select>
      <Languages className="h-3.5 w-3.5 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}

function HeaderSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setQ("");
  };
  return (
    <form onSubmit={submit} className="hidden md:block relative">
      <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        data-testid="header-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search ads, phones, @handles…"
        className="h-9 w-64 pl-8 pr-3 text-sm border border-slate-200 rounded-md bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-colors"
      />
    </form>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login");
  };

  const allNav = [...BASE_NAV, ...ADMIN_NAV];
  const currentLabel =
    allNav.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))?.label || "MUKTI";

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-slate-900 lg:flex">
      <aside className="hidden lg:flex w-60 shrink-0 border-r border-slate-200 bg-white flex-col">
        <SidebarContent user={user} onLogout={onLogout} suffix="desktop" />
      </aside>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 sm:w-80">
          <SheetHeader className="sr-only"><SheetTitle>Navigation</SheetTitle></SheetHeader>
          <SidebarContent user={user} onLogout={onLogout} onNavigate={() => setOpen(false)} suffix="mobile" />
        </SheetContent>

        <main className="flex-1 min-w-0 flex flex-col">
          <header className="h-14 border-b border-slate-200 bg-white px-4 sm:px-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <SheetTrigger asChild>
                <button type="button" data-testid="mobile-menu-btn" aria-label="Open navigation"
                  className="lg:hidden h-9 w-9 grid place-items-center rounded-md border border-slate-200 hover:bg-slate-50">
                  <Menu className="h-4 w-4" />
                </button>
              </SheetTrigger>
              <div className="lg:hidden font-display font-bold text-sm tracking-tight truncate">{currentLabel}</div>
              <div className="hidden lg:flex items-center gap-3 text-xs text-slate-500">
                <ShieldCheck className="h-4 w-4 text-blue-600" />
                <span className="font-mono uppercase tracking-wider">Investigative use only · Bangalore · EN</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <HeaderSearch />
              <LanguageSwitcher />
              <NotificationBell />
              <div className="text-[11px] sm:text-xs text-slate-500 font-mono whitespace-nowrap">v1.1</div>
            </div>
          </header>

          <div className="flex-1 overflow-auto">
            <Outlet />
          </div>
        </main>
      </Sheet>
    </div>
  );
}
